import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Mail, KeyRound, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import MiniFooter from "../components/MiniFooter";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: code+password, 3: done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success(r.data?.message || "Si el email existe, recibirás un código");
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos procesar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success("Nuevo código enviado");
    } catch {
      toast.error("No pudimos reenviar el código");
    } finally {
      setLoading(false);
    }
  };

  const resetPwd = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    if (newPassword !== confirmPassword) { toast.error("Las contraseñas no coinciden"); return; }
    if (!/^\d{6}$/.test(code)) { toast.error("El código debe tener 6 dígitos"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        new_password: newPassword,
      });
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Código incorrecto o expirado");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="forgot-password-page">
      <header className="px-6 py-5 border-b border-zinc-200 bg-white">
        <Link to="/" className="flex items-center w-fit">
          <img src="/logo-brand.png" alt="DerechoLlave" className="h-14 w-auto" />
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/login" className="text-sm text-slate-500 inline-flex items-center gap-1 mb-6 hover:underline" data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio de sesión
          </Link>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`flex-1 h-1 rounded-full transition ${step >= n ? "bg-amber-500" : "bg-zinc-200"}`} />
            ))}
          </div>

          {step === 1 && (
            <>
              <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mb-4">
                <Mail className="w-6 h-6" />
              </div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight">Recupera tu contraseña</h1>
              <p className="text-slate-500 mt-2 text-sm">
                Ingresa el email de tu cuenta. Te enviaremos un código de 6 dígitos para crear una nueva contraseña.
              </p>

              <form onSubmit={requestCode} className="mt-7 space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email" type="email" required autoFocus
                    placeholder="tucorreo@ejemplo.cl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full btn-primary h-11" data-testid="forgot-send-code-btn">
                  {loading ? "Enviando..." : "Enviar código"}
                </Button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mb-4">
                <KeyRound className="w-6 h-6" />
              </div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight">Ingresa el código</h1>
              <p className="text-slate-500 mt-2 text-sm">
                Enviamos un código de 6 dígitos a <strong className="text-slate-800">{email}</strong>. Vence en 15 minutos.
              </p>

              <form onSubmit={resetPwd} className="mt-7 space-y-4">
                <div>
                  <Label htmlFor="code">Código de 6 dígitos</Label>
                  <Input
                    id="code" type="text" inputMode="numeric" maxLength={6} required autoFocus
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="text-center font-mono text-2xl tracking-[0.5em]"
                    data-testid="forgot-code-input"
                  />
                </div>
                <div>
                  <Label htmlFor="new-password">Nueva contraseña</Label>
                  <Input
                    id="new-password" type="password" required minLength={6}
                    placeholder="Mínimo 6 caracteres"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="forgot-new-password"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirmar contraseña</Label>
                  <Input
                    id="confirm-password" type="password" required minLength={6}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="forgot-confirm-password"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full btn-primary h-11" data-testid="forgot-reset-btn">
                  {loading ? "Validando..." : "Restablecer contraseña"}
                </Button>
                <div className="flex items-center justify-between text-xs">
                  <button type="button" onClick={() => setStep(1)} className="text-slate-500 hover:underline" data-testid="forgot-change-email">
                    Cambiar email
                  </button>
                  <button type="button" onClick={resend} disabled={loading} className="text-slate-700 font-semibold hover:underline disabled:opacity-50" data-testid="forgot-resend-code">
                    Reenviar código
                  </button>
                </div>
              </form>
            </>
          )}

          {step === 3 && (
            <div className="text-center" data-testid="forgot-success">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4 mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight">¡Contraseña actualizada!</h1>
              <p className="text-slate-500 mt-2 text-sm">
                Ya puedes iniciar sesión con tu nueva contraseña.
              </p>
              <Button onClick={() => navigate("/login")} className="w-full btn-primary h-11 mt-7" data-testid="forgot-go-login">
                Ir al inicio de sesión
              </Button>
            </div>
          )}

          <div className="mt-8 bg-white border border-zinc-200 rounded-lg p-4 text-xs text-slate-500 flex items-start gap-2.5">
            <ShieldCheck className="w-4 h-4 text-slate-700 shrink-0 mt-0.5" />
            <p>
              Por tu seguridad, nunca compartas el código con nadie. El equipo de DerechoLlave nunca te pedirá este código por teléfono ni WhatsApp.
            </p>
          </div>
        </div>
      </main>
      <MiniFooter />
    </div>
  );
}
